import { Checkbox, Divider, ListSubheader, MenuItem } from "@mui/material"
import { Fragment, memo, useCallback, useMemo } from "react"

import { FormField } from "@/components/Forms"
import MultiSelect from "@/components/MultiSelect/MultiSelect"
import { PermissionActionType } from "@/constants/permissions"
import { usePermissionManager } from "@/hooks/usePermissionManager"
import { validationCategoryCompanyType, validationCompanyType } from "@/screens/Companies/utils/company"
import type { TreeNode } from "@/types/tree"
import { flattenTree } from "@/utils/utils"

interface CompanyTypeSelectProps {
  companyTypeTree: TreeNode<string>[]
  name: string
  disabled?: boolean
}

interface CategoryItemProps {
  category: TreeNode<string>
  field: { value: string[]; onChange: (value: string[]) => void }
  handleToggle: (field: { value: string[]; onChange: (value: string[]) => void }, value: string) => void
}

const CategoryItem = memo(({ category, field, handleToggle }: CategoryItemProps) => (
  <MenuItem key={category.id} value={category.id} className='pl-4' onClick={() => handleToggle(field, category.id)}>
    <Checkbox checked={field.value.includes(category.id)} onChange={() => handleToggle(field, category.id)} />
    {category.name}
  </MenuItem>
))

interface SubCategoryItemsProps {
  node: TreeNode<string>
  field: { value: string[]; onChange: (value: string[]) => void }
  handleToggle: (field: { value: string[]; onChange: (value: string[]) => void }, value: string) => void
  getNodeDepth: (nodeId: string) => number
  disabled?: boolean
}

const ItemsList = ({ node, field, handleToggle, getNodeDepth, disabled }: SubCategoryItemsProps) => {
  return node.children?.map((node) => (
    <Fragment key={node.id}>
      <MenuItem
        key={node.id}
        value={node.id}
        sx={{ paddingLeft: `${16 + 32 * getNodeDepth(node.id)}px` }}
        onClick={() => handleToggle(field, node.id)}
        disabled={validationCompanyType(field, node, disabled)}
      >
        <Checkbox checked={field.value.includes(node.id)} onChange={() => handleToggle(field, node.id)} />
        {node.name}
      </MenuItem>
      {node?.children ? (
        <ItemsList
          node={node}
          field={field}
          handleToggle={handleToggle}
          getNodeDepth={getNodeDepth}
          disabled={validationCompanyType(field, node, disabled)}
        />
      ) : null}
    </Fragment>
  ))
}

const SubCategoryItems = ({ node, field, handleToggle, getNodeDepth }: SubCategoryItemsProps) => {
  const shouldDisableCategory = validationCategoryCompanyType(field, node)

  return (
    <>
      <ListSubheader key={`${node.id}-subheader`} className='py-1 pl-4 font-medium'>
        {node.name}
      </ListSubheader>
      {node.children ? (
        <ItemsList
          node={node}
          field={field}
          handleToggle={handleToggle}
          getNodeDepth={getNodeDepth}
          disabled={shouldDisableCategory}
        />
      ) : null}
    </>
  )
}

const CompanyTypeSelect = ({ companyTypeTree, name, disabled }: CompanyTypeSelectProps) => {
  const { hasPermissions } = usePermissionManager()
  const isLeafNode = useCallback((node: TreeNode<string>) => !node.children || node.children.length === 0, [])
  const options = useMemo(() => flattenTree(companyTypeTree), [companyTypeTree])

  const getNodeDepth = useCallback(
    (nodeId: string) => options.find((option) => option.value === nodeId)?.depth ?? 0,
    [options]
  )

  const handleToggle = useCallback(
    (field: { value: string[]; onChange: (value: string[]) => void }, value: string) => {
      const toggledField = options.find((option) => option.value === value)
      if (!toggledField) return

      const isValueSelected = field.value.includes(value)
      const newValue = isValueSelected ? field.value.filter((v: string) => v !== value) : [...field.value, value]

      field.onChange(newValue)
    },
    [options]
  )

  const shouldShowCategory = useCallback(
    (category: TreeNode<string>) => {
      if (category.id === "VENDOR") {
        return hasPermissions([
          PermissionActionType.VENDOR_MAINTENANCE,
          PermissionActionType.VENDOR_LOCATION_ASSOCIATION,
          PermissionActionType.VENDOR_PRODUCT_MAINTENANCE,
          PermissionActionType.VENDOR_VIEW,
        ])
      }

      return true
    },
    [hasPermissions]
  )

  return (
    <FormField
      name={name}
      label='Company Type'
      required
      render={({ field, fieldState: { error } }) => (
        <MultiSelect
          id='company-type-select'
          name={name}
          value={field.value}
          onChange={field.onChange}
          disabled={disabled}
          options={options}
          error={!!error}
        >
          {companyTypeTree
            .filter(shouldShowCategory)
            .map((category, index) => [
              index > 0 && <Divider key={`divider-${category.id}`} className='my-2' />,
              isLeafNode(category) ? (
                <CategoryItem key={category.id} category={category} field={field} handleToggle={handleToggle} />
              ) : (
                <SubCategoryItems
                  key={category.id}
                  node={category}
                  field={field}
                  handleToggle={handleToggle}
                  getNodeDepth={getNodeDepth}
                />
              ),
            ])}
        </MultiSelect>
      )}
    />
  )
}

export default memo(CompanyTypeSelect)
